import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuButton from './MenuButton';
import Search from './Search';
import ThemeToggle from './ThemeToggle';
import LiveClock from './LiveClock';
import { getCollection, requestJson } from '../../lib/api';
import { getSocket } from '../../lib/socket';
import { playNotificationSound } from '../../lib/sound';
import { useSiteSession } from '../../context/SiteSessionContext';
import { useCoalescedRefresh } from '../../hooks/useCoalescedRefresh';

function getReferenceId(value) {
  return value?._id || value?.id || value;
}

function buildDispatchListNotification(list) {
  const direction = list.direction === 'workshop_to_store' ? 'Atelier → Magasin' : 'Magasin → Atelier';
  const itemCount = Array.isArray(list.items) ? list.items.length : 0;
  return {
    type: 'dispatch-list',
    id: `dispatch-list:${list._id}`,
    listNumber: list.listNumber || 'BL',
    title: 'BL à réceptionner',
    category: 'À réceptionner',
    message: `${direction} • ${itemCount} dossier${itemCount > 1 ? 's' : ''}`,
  };
}

function buildStoreCommentNotification(item) {
  const count = Number(item.unreadStoreReplyCount || 0);
  const store = item.siteId?.code || item.siteId?.name || 'Magasin';
  const reference = item.requestNumber || 'Dossier Logistique';
  return {
    type: 'logistics',
    id: `logistics-comment:${item._id}:${item.lastStoreReplyAt || count}`,
    savRequestId: item._id,
    reference,
    listNumber: reference,
    category: 'Nouveau commentaire',
    count,
    message: `${store} • ${count} commentaire${count > 1 ? 's' : ''} non lu${count > 1 ? 's' : ''}`,
  };
}

function buildStoreWorkshopIssueNotification(item) {
  const issueLabels = [];
  if (item.hasNca) issueLabels.push('NCA');
  if (item.hasTraceabilityNon) issueLabels.push('Traçabilité montage non');
  const job = item.jobNumber || item.workshopSheetNumber || 'JOB';
  const store = item.storeSiteId?.code || item.storeSiteId?.name || 'Magasin';
  const category = issueLabels.join(' + ') || 'Anomalie atelier';
  return {
    type: 'workshop-issue',
    id: `workshop-issue:${item._id}`,
    commandeId: item._id,
    jobNumber: job,
    reference: job,
    listNumber: job,
    category,
    count: 1,
    message: `${store} • ${category} avant réception magasin`,
  };
}

function NotificationBell() {
  const navigate = useNavigate();
  const { isAdmin, site, siteType } = useSiteSession();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info', notification: null });
  const initializedRef = useRef(false);
  const notificationIdsRef = useRef(new Set());

  const loadNotifications = useCallback(
    async ({ popup = false } = {}) => {
      if (!isAdmin && (!site?._id || !siteType)) {
        setNotifications([]);
        notificationIdsRef.current = new Set();
        initializedRef.current = true;
        return;
      }

      const [dispatchListsPayload, storeCommentsPayload, storeWorkshopIssuesPayload] = await Promise.all([
        requestJson('/commandes/workflow/dispatch-lists'),
        isAdmin || siteType === 'sav'
          ? requestJson('/sav-requests/notifications/store-comments')
          : Promise.resolve({ data: [] }),
        isAdmin || siteType === 'store'
          ? requestJson('/commandes/workflow/store-pending-issues')
          : Promise.resolve({ data: [] }),
      ]);
      const expectedDirection = siteType === 'workshop' ? 'store_to_workshop' : 'workshop_to_store';
      const siteField = siteType === 'workshop' ? 'workshopSiteId' : 'storeSiteId';
      const dispatchNotifications = getCollection(dispatchListsPayload)
        .filter((list) => {
          const pendingReceipt = list.status === 'sent' || list.status === 'pending';
          return pendingReceipt && list.direction === expectedDirection && String(getReferenceId(list[siteField])) === String(site._id);
        })
        .map(buildDispatchListNotification);
      const storeCommentNotifications = getCollection(storeCommentsPayload).map(buildStoreCommentNotification);
      const storeWorkshopIssueNotifications = getCollection(storeWorkshopIssuesPayload).map(buildStoreWorkshopIssueNotification);
      const pending = [...storeCommentNotifications, ...storeWorkshopIssueNotifications, ...dispatchNotifications];

      const nextIds = new Set(pending.map((item) => item.id));
      const previousIds = notificationIdsRef.current;
      const fresh = pending.filter((item) => !previousIds.has(item.id));
      const hasMorePending = pending.length > previousIds.size;

      setNotifications(pending);
      notificationIdsRef.current = nextIds;

      if (popup && initializedRef.current && (fresh.length || hasMorePending)) {
        playNotificationSound();
        const firstFresh = fresh[0] || pending[0];
        const freshCount = fresh.length || pending.length;
        setSnackbar({
          open: true,
          severity: 'info',
          message: firstFresh.type === 'logistics'
            ? freshCount === 1
              ? `Nouveau commentaire magasin : ${firstFresh.reference}`
              : `${freshCount} dossiers Logistique ont de nouveaux commentaires.`
            : firstFresh.type === 'workshop-issue'
              ? freshCount === 1
                ? `Anomalie atelier à recevoir : ${firstFresh.reference}`
                : `${freshCount} dossiers avec anomalie atelier à recevoir.`
            : freshCount === 1
              ? `BL à réceptionner : ${firstFresh.listNumber}`
              : `${freshCount} BL à réceptionner.`,
          notification: firstFresh,
        });
      }
      initializedRef.current = true;
    },
    [isAdmin, site, siteType]
  );

  useEffect(() => {
    loadNotifications().catch(() => {});
  }, [loadNotifications]);

  const scheduleNotificationRefresh = useCoalescedRefresh(
    () => loadNotifications({ popup: true }),
    350
  );

  useEffect(() => {
    const socket = getSocket();
    const handler = () => scheduleNotificationRefresh();
    socket.on('commandes:changed', handler);
    socket.on('sav-requests:changed', handler);
    window.addEventListener('opticdev:logistics-updated', handler);
    return () => {
      socket.off('commandes:changed', handler);
      socket.off('sav-requests:changed', handler);
      window.removeEventListener('opticdev:logistics-updated', handler);
    };
  }, [scheduleNotificationRefresh]);

  const groupedCount = useMemo(
    () => notifications.reduce((total, notification) => total + Math.max(1, Number(notification.count || 1)), 0),
    [notifications]
  );

  const openNotification = (notification) => {
    if (!notification) return;
    setAnchorEl(null);
    setSnackbar((current) => ({ ...current, open: false }));

    if (notification.type === 'logistics') {
      const params = new URLSearchParams();
      if (notification.savRequestId) {
        params.set('open', notification.savRequestId);
        params.set('mode', 'history');
      } else if (notification.reference) {
        params.set('q', notification.reference);
      }
      navigate(`/sav?${params.toString()}`);
      return;
    }

    if (notification.type === 'dispatch-list') {
      navigate('/suivi-atelier/bl');
      return;
    }

    const pathname = '/suivi-atelier';
    const params = new URLSearchParams();

    if (notification.jobNumber && notification.jobNumber !== '—') params.set('job', notification.jobNumber);
    if (notification.commandeId) params.set('commandeId', notification.commandeId);
    if (notification.stepCode) params.set('step', notification.stepCode);

    navigate(`${pathname}?${params.toString()}`);
  };

  return (
    <>
      <MenuButton
        showBadge={groupedCount > 0}
        badgeContent={groupedCount > 0 ? groupedCount : undefined}
        aria-label="Notifications"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <NotificationsRoundedIcon />
      </MenuButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: { xs: 'calc(100vw - 32px)', sm: 560, md: 680 }, maxWidth: 'calc(100vw - 32px)', mt: 1, border: '1px solid', borderColor: 'divider' } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                Notifications
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Actions attendues pour le site connecté.
              </Typography>
            </Box>
            <Chip size="small" label={groupedCount} color={groupedCount ? 'error' : 'default'} />
          </Stack>
        </Box>
        <Divider />
        {notifications.length ? (
          <List dense disablePadding sx={{ maxHeight: 560, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <ListItemButton key={notification.id} onClick={() => openNotification(notification)} sx={{ py: 1.2 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        {notification.listNumber || notification.jobNumber}
                      </Typography>
                      <Chip
                        size="small"
                        label={notification.category}
                        color={notification.type === 'workshop-issue' ? 'error' : 'primary'}
                        variant="outlined"
                      />
                    </Stack>
                  }
                  secondary={notification.message}
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
            Aucune action en attente.
          </Box>
        )}
        <Divider />
        <Box sx={{ p: 1.5, textAlign: 'right' }}>
          <Button size="small" onClick={() => loadNotifications().catch(() => {})}>
            Rafraîchir
          </Button>
        </Box>
      </Popover>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          onClick={(event) => {
            if (event.target.closest('button')) return;
            openNotification(snackbar.notification);
          }}
          sx={{
            minWidth: 360,
            boxShadow: 8,
            fontWeight: 800,
            cursor: snackbar.notification ? 'pointer' : 'default',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default function Header() {
  return (
    <Stack
      direction="row"
      sx={{
        display: { xs: 'none', md: 'flex' },
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        pt: 0.5,
      }}
      spacing={2}
    >
      <Box sx={{ width: { md: 180, lg: 220 }, flexShrink: 0 }} />
      <Stack sx={{ width: { md: 420, lg: 560 }, mx: 'auto' }}>
        <Search />
      </Stack>
      <Stack direction="row" sx={{ gap: 1, width: { md: 180, lg: 220 }, justifyContent: 'flex-end', flexShrink: 0 }}>
        <LiveClock />
        <ThemeToggle />
        <NotificationBell />
      </Stack>
    </Stack>
  );
}
